import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import TextField from "../TextField";
import Button from "../Button";
import { Typography } from "../Typography";
import HorizontalFormField from "../HorizontalFormField";

const Form = ({
  fields = [],
  initialValues = {},
  onSubmit,
  onCancel,
  onChange,
  submitLabel = "Save",
  cancelLabel = "Cancel",
  showActions = true,
  className = "",
  layout = "vertical", // vertical, horizontal, grid, horizontal-label
  gridColumns = 2,
  loading = false,
}) => {
  const [formData, setFormData] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPasswords, setShowPasswords] = useState({});

  // Update form data when initialValues prop changes (avoid loop: only set when reference changed)
  const prevInitialRef = React.useRef(initialValues);
  useEffect(() => {
    if (initialValues && initialValues !== prevInitialRef.current) {
      prevInitialRef.current = initialValues;
      setFormData(initialValues);
    }
  }, [initialValues]);

  const handleInputChange = (fieldName, value) => {
    setFormData((prev) => {
      const newData =
        typeof fieldName === "object" && fieldName !== null
          ? { ...prev, ...fieldName }
          : { ...prev, [fieldName]: value };
      // Defer parent sync to avoid "Cannot update component while rendering"
      if (onChange) {
        const data = newData;
        setTimeout(() => onChange(data), 0);
      }
      return newData;
    });

    // Clear error when user starts typing
    const fieldsToClear =
      typeof fieldName === "object" && fieldName !== null ? Object.keys(fieldName) : [fieldName];
    const hasErrorsToClear = fieldsToClear.some((f) => errors[f]);
    if (hasErrorsToClear) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        fieldsToClear.forEach((f) => delete newErrors[f]);
        return newErrors;
      });
    }
  };

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
    validateField(fieldName, formData[fieldName]);
  };

  const validateField = (fieldName, value) => {
    const field = fields.find((f) => f.name === fieldName);
    if (!field) return;

    const fieldErrors = [];

    // Required validation
    if (field.required && (!value || (typeof value === "string" && !value.trim()))) {
      fieldErrors.push(`${field.label || fieldName} is required`);
    }

    // Custom validation
    if (field.validate && value) {
      const customError = field.validate(value, formData);
      if (customError) {
        fieldErrors.push(customError);
      }
    }

    // Min/Max length validation
    if (field.minLength && value && value.length < field.minLength) {
      fieldErrors.push(
        `${field.label || fieldName} must be at least ${field.minLength} characters`
      );
    }
    if (field.maxLength && value && value.length > field.maxLength) {
      fieldErrors.push(
        `${field.label || fieldName} must be no more than ${field.maxLength} characters`
      );
    }

    // Email validation
    if (field.type === "email" && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        fieldErrors.push("Please enter a valid email address");
      }
    }

    if (fieldErrors.length > 0) {
      setErrors((prev) => ({ ...prev, [fieldName]: fieldErrors[0] }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    fields.forEach((field) => {
      const value = formData[field.name];
      validateField(field.name, value);
      if (errors[field.name]) {
        newErrors[field.name] = errors[field.name];
      }
    });
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    // Mark all fields as touched
    const allTouched = {};
    fields.forEach((field) => {
      allTouched[field.name] = true;
    });
    setTouched(allTouched);

    // Validate all fields
    fields.forEach((field) => {
      validateField(field.name, formData[field.name]);
    });

    if (validateForm() && onSubmit) {
      onSubmit(formData);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      // Reset form to initial values
      setFormData(initialValues);
      setErrors({});
      setTouched({});
    }
  };

  const renderField = (field) => {
    const value = formData[field.name] || "";
    const error = errors[field.name];
    const isTouched = touched[field.name];

    // For horizontal-label layout, use HorizontalFormField
    if (layout === "horizontal-label") {
      // Skip hidden fields
      if (field.type === "hidden") {
        return null;
      }

      // Handle custom render for horizontal layout
      if (field.render) {
        return (
          <div key={field.name} className={field.className || ""}>
            <HorizontalFormField
              label={field.label || ""}
              name={field.name}
              type={field.type || "text"}
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              onBlur={() => handleBlur(field.name)}
              error={error && isTouched ? error : undefined}
              helperText={field.helperText}
              required={field.required}
              disabled={field.disabled}
              placeholder={field.placeholder}
              multiline={field.type === "textarea"}
              rows={field.rows}
              options={field.options}
              startIcon={field.startIcon}
              infoIcon={field.infoIcon}
              className={field.className}
              formData={formData}
              render={(val, fd, hIC, err, errs, tch) => field.render(val, fd, hIC, err, errs || errors, tch || touched, handleBlur)}
              errors={errors}
              touched={touched}
              handleInputChange={handleInputChange}
              handleBlur={handleBlur}
              maxLength={field.maxLength}
            />
          </div>
        );
      }

      if (field.type === "custom") {
        return (
          <div key={field.name} className={field.className || ""}>
            {field.component}
          </div>
        );
      }

      // Handle file type specially for horizontal layout
      if (field.type === "file") {
        return (
          <div key={field.name} className={field.className || ""}>
            <div className="flex items-start gap-6 py-4">
              <div className="flex-shrink-0 w-48">
                <div className="flex items-center gap-2">
                  {field.label && (
                    <label className="text-sm font-medium text-slate-700 text-left">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                  )}
                  {field.infoIcon && (
                    <Icon
                      icon={field.infoIcon}
                      className="h-4 w-4 text-slate-400"
                      title={field.helperText}
                    />
                  )}
                </div>
                {field.helperText && !field.infoIcon && (
                  <Typography as="p" className="text-xs text-tertiary-600 mt-1">
                    {field.helperText}
                  </Typography>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col gap-2">
                  {field.preview && (
                    <div className={field.previewClassName || "h-20 w-20 rounded-full overflow-hidden border-2 border-slate-200"}>
                      <img
                        src={field.preview}
                        alt="Preview"
                        className="h-full w-full object-cover"
                      />
                    </div>
                  )}
                  <label
                    className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed ${
                      field.uploadBorderColor || "border-purple-300"
                    } rounded-lg cursor-pointer ${
                      field.uploadBgColor || "bg-purple-50 hover:bg-purple-100"
                    } transition-colors`}
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Icon
                        icon={field.uploadIcon || "lucide:upload"}
                        className={`h-8 w-8 ${field.uploadIconColor || "text-purple-600"} mb-2`}
                      />
                      <p className="mb-2 text-sm text-slate-700">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-tertiary-600">
                        {field.acceptText || "SVG, PNG, JPG or GIF (max. 800x400px)"}
                      </p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept={field.accept || "image/*"}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (field.onFileChange) {
                            field.onFileChange(file, (preview) => {
                              handleInputChange(field.name, preview);
                            });
                          } else {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              handleInputChange(field.name, reader.result);
                            };
                            reader.readAsDataURL(file);
                          }
                        }
                      }}
                    />
                  </label>
                </div>
                {error && isTouched && (
                  <p className="text-xs text-red-500 mt-1">{error}</p>
                )}
              </div>
            </div>
          </div>
        );
      }

      return (
        <div key={field.name} className={field.className || ""}>
          <HorizontalFormField
            label={field.label}
            name={field.name}
            type={field.type || "text"}
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            onBlur={() => handleBlur(field.name)}
            error={error && isTouched ? error : undefined}
            helperText={field.helperText}
            required={field.required}
            disabled={field.disabled}
            placeholder={field.placeholder}
            multiline={field.type === "textarea"}
            rows={field.rows}
            errors={errors}
            touched={touched}
            formData={formData}
            handleInputChange={handleInputChange}
            handleBlur={handleBlur}
            options={field.options}
            startIcon={field.startIcon}
            infoIcon={field.infoIcon}
            maxLength={field.maxLength}
          />
        </div>
      );
    }

    // Custom render function
    if (field.render) {
      return field.render(value, formData, handleInputChange, error);
    }

    switch (field.type) {
      case "text":
      case "email":
      case "password":
      case "number":
      case "tel":
      case "url":
        const isPasswordField = field.type === "password";
        const inputType = isPasswordField && showPasswords[field.name] ? "text" : field.type;
        
        return (
          <div key={field.name} className={field.className || ""}>
            {field.startIcon && (
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-700 text-left">
                  {field.label}
                  {field.required && <span className="text-red-500">*</span>}
                </label>
                <div className="relative">
                  <Icon
                    icon={field.startIcon}
                    className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"
                  />
                  <input
                    type={inputType}
                    name={field.name}
                    value={value}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    onBlur={() => handleBlur(field.name)}
                    placeholder={field.placeholder}
                    disabled={field.disabled}
                    required={field.required}
                    className={`w-full rounded-lg border ${
                      error && isTouched
                        ? "border-red-400"
                        : "border-slate-200"
                    } bg-white ${
                      field.startIcon ? "pl-10" : "pl-4"
                    } ${isPasswordField ? "pr-10" : "pr-4"} py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue disabled:bg-slate-100 disabled:cursor-not-allowed`}
                  />
                  {isPasswordField && (
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, [field.name]: !prev[field.name] }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 focus:outline-none"
                      tabIndex={-1}
                    >
                      <Icon
                        icon={showPasswords[field.name] ? 'lucide:eye-off' : 'lucide:eye'}
                        className="h-5 w-5"
                      />
                    </button>
                  )}
                </div>
                {field.helperText && (
                  <p className="text-xs text-slate-500 mt-1">{field.helperText}</p>
                )}
                {error && isTouched && (
                  <p className="text-xs text-red-500 mt-1">{error}</p>
                )}
              </div>
            )}

            {!field.startIcon && (
              <TextField
                label={field.label}
                name={field.name}
                type={field.type}
                value={value}
                onChange={(e) => handleInputChange(field.name, e.target.value)}
                onBlur={() => handleBlur(field.name)}
                error={error && isTouched}
                helperText={error && isTouched ? error : field.helperText}
                required={field.required}
                disabled={field.disabled}
                placeholder={field.placeholder}
                fullWidth={field.fullWidth !== false}
                className={field.inputClassName}
                showPasswordToggle={isPasswordField}
              />
            )}
          </div>
        );

      case "textarea":
        return (
          <div key={field.name} className={field.className || ""}>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-slate-700 text-left">
                {field.label}
                {field.required && <span className="text-red-500">*</span>}
              </label>
              {field.helperText && (
                <Typography as="p" className="text-xs text-tertiary-600">
                  {field.helperText}
                </Typography>
              )}
              <TextField
                name={field.name}
                multiline
                rows={field.rows || 4}
                value={value}
                onChange={(e) => handleInputChange(field.name, e.target.value)}
                onBlur={() => handleBlur(field.name)}
                error={error && isTouched}
                placeholder={field.placeholder}
                disabled={field.disabled}
                className={`resize-none ${field.inputClassName || ""}`}
              />
              {field.maxLength && (
                <Typography
                  as="p"
                  className={`text-xs ${
                    value.length > field.maxLength
                      ? "text-red-500"
                      : "text-tertiary-600"
                  }`}
                >
                  {field.maxLength - value.length} characters left
                </Typography>
              )}
              {error && isTouched && !field.maxLength && (
                <p className="text-xs text-red-500 mt-1">{error}</p>
              )}
            </div>
          </div>
        );

      case "select":
        return (
          <div key={field.name} className={field.className || ""}>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-700 text-left">
                  {field.label}
                  {field.required && <span className="text-red-500">*</span>}
                </label>
                {field.infoIcon && (
                  <Icon
                    icon={field.infoIcon}
                    className="h-4 w-4 text-slate-400"
                  />
                )}
              </div>
              <div className="relative">
                {field.startIcon && (
                  <Icon
                    icon={field.startIcon}
                    className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"
                  />
                )}
                <select
                  name={field.name}
                  value={value}
                  onChange={(e) => handleInputChange(field.name, e.target.value)}
                  onBlur={() => handleBlur(field.name)}
                  disabled={field.disabled}
                  required={field.required}
                  className={`w-full rounded-lg border ${
                    error && isTouched
                      ? "border-red-400"
                      : "border-slate-200"
                  } bg-white ${
                    field.startIcon ? "pl-10" : "pl-4"
                  } pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none disabled:bg-slate-100 disabled:cursor-not-allowed`}
                >
                  {field.placeholder && (
                    <option value="">{field.placeholder}</option>
                  )}
                  {field.options?.map((option) => (
                    <option
                      key={typeof option === "object" ? option.value : option}
                      value={typeof option === "object" ? option.value : option}
                    >
                      {typeof option === "object" ? option.label : option}
                    </option>
                  ))}
                </select>
                <Icon
                  icon="lucide:chevron-down"
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                />
              </div>
              {field.helperText && (
                <p className="text-xs text-slate-500 mt-1">{field.helperText}</p>
              )}
              {error && isTouched && (
                <p className="text-xs text-red-500 mt-1">{error}</p>
              )}
            </div>
          </div>
        );

      case "file":
        return (
          <div key={field.name} className={field.className || ""}>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-700 text-left">
                  {field.label}
                  {field.required && <span className="text-red-500">*</span>}
                </label>
                {field.infoIcon && (
                  <Icon
                    icon={field.infoIcon}
                    className="h-4 w-4 text-slate-400"
                  />
                )}
              </div>
              {field.helperText && (
                <Typography as="p" className="text-xs text-tertiary-600">
                  {field.helperText}
                </Typography>
              )}
              {field.preview && (
                <div className={field.previewClassName || "h-20 w-20 rounded-full overflow-hidden border-2 border-slate-200"}>
                  <img
                    src={field.preview}
                    alt="Preview"
                    className="h-full w-full object-cover"
                  />
                </div>
              )}
              <label
                className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed ${
                  field.uploadBorderColor || "border-purple-300"
                } rounded-lg cursor-pointer ${
                  field.uploadBgColor || "bg-purple-50 hover:bg-purple-100"
                } transition-colors`}
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Icon
                    icon={field.uploadIcon || "lucide:upload"}
                    className={`h-8 w-8 ${field.uploadIconColor || "text-purple-600"} mb-2`}
                  />
                  <p className="mb-2 text-sm text-slate-700">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-tertiary-600">
                    {field.acceptText || "SVG, PNG, JPG or GIF (max. 800x400px)"}
                  </p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept={field.accept || "image/*"}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (field.onFileChange) {
                        field.onFileChange(file, (preview) => {
                          handleInputChange(field.name, preview);
                        });
                      } else {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          handleInputChange(field.name, reader.result);
                        };
                        reader.readAsDataURL(file);
                      }
                    }
                  }}
                />
              </label>
              {error && isTouched && (
                <p className="text-xs text-red-500 mt-1">{error}</p>
              )}
            </div>
          </div>
        );

      case "custom":
        return (
          <div key={field.name} className={field.className || ""}>
            {field.component}
          </div>
        );

      default:
        return (
          <div key={field.name} className={field.className || ""}>
            <TextField
              label={field.label}
              name={field.name}
              type={field.type || "text"}
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              onBlur={() => handleBlur(field.name)}
              error={error && isTouched}
              helperText={error && isTouched ? error : field.helperText}
              required={field.required}
              disabled={field.disabled}
              placeholder={field.placeholder}
              fullWidth={field.fullWidth !== false}
            />
          </div>
        );
    }
  };

  const getLayoutClass = () => {
    switch (layout) {
      case "grid":
        const gridCols = {
          1: "grid-cols-1",
          2: "grid-cols-1 sm:grid-cols-2",
          3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
          4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
        };
        return `grid ${gridCols[gridColumns] || gridCols[2]} gap-4`;
      case "horizontal":
        return "flex flex-col sm:flex-row gap-4";
      case "horizontal-label":
        return "flex flex-col gap-0 divide-y divide-slate-200";
      default:
        return "flex flex-col gap-6";
    }
  };

  return (
    <form onSubmit={handleSubmit} className={className}>
      <div className={getLayoutClass()}>
        {fields.map((field) => renderField(field))}
      </div>

      {showActions && (
        <div className="flex items-center justify-end gap-3 mt-6">
          {onCancel && (
            <Button
              type="button"
              variant="secondary"
              size="medium"
              onClick={handleCancel}
            >
              {cancelLabel}
            </Button>
          )}
          <Button type="submit" variant="primary" size="medium" loading={loading}>
            {submitLabel}
          </Button>
        </div>
      )}
    </form>
  );
};

export default Form;

